import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface RecentActivity {
  id: string;
  property_address: string;
  activity_type: 'view' | 'created' | 'status_change' | 'updated' | 'task_created' | 'activity_logged';
  description: string;
  timestamp: string;
  property_id?: string;
  icon_type?: string;
}

const formatTimeAgo = (timestamp: string) => {
  const now = new Date();
  const activityTime = new Date(timestamp);
  const diffInMs = now.getTime() - activityTime.getTime();
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    return diffInMinutes < 1 ? 'just now' : `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  } else {
    return `${diffInDays}d ago`;
  }
};

const formatDate = (timestamp: string) => {
  const date = new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

export const useRecentActivity = () => {
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchRecentActivities = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      const allActivities: RecentActivity[] = [];
      const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();

      // Fetch recent property views (last 14 days)
      const { data: viewsData, error: viewsError } = await supabase
        .from('property_views')
        .select(`
          id,
          viewed_at,
          properties!inner(
            id,
            address,
            seller_id
          )
        `)
        .eq('properties.seller_id', user.id)
        .not('buyer_id', 'is', null)
        .gte('viewed_at', fourteenDaysAgo)
        .order('viewed_at', { ascending: false })
        .limit(10);

      if (viewsError) {
        console.error('Error fetching property views:', viewsError);
      } else if (viewsData) {
        viewsData.forEach(view => {
          const property = Array.isArray(view.properties) ? view.properties[0] : view.properties;
          if (property) {
            allActivities.push({
              id: `view-${view.id}`,
              property_address: property.address,
              activity_type: 'view',
              description: 'New view from potential investor',
              timestamp: view.viewed_at,
              property_id: property.id,
              icon_type: 'view',
            });
          }
        });
      }

      // Fetch recent property creations (last 14 days)
      const { data: propertiesData, error: propertiesError } = await supabase
        .from('properties')
        .select('id, address, created_at, updated_at, status')
        .eq('seller_id', user.id)
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false })
        .limit(5);

      if (propertiesError) {
        console.error('Error fetching properties:', propertiesError);
      } else if (propertiesData) {
        propertiesData.forEach(property => {
          allActivities.push({
            id: `created-${property.id}`,
            property_address: property.address,
            activity_type: 'created',
            description: 'New property created',
            timestamp: property.created_at,
            property_id: property.id,
            icon_type: 'created',
          });

          if (property.status === 'sold' || property.status === 'closed') {
            allActivities.push({
              id: `status-${property.id}`,
              property_address: property.address,
              activity_type: 'status_change',
              description: `Deal marked as ${property.status}`,
              timestamp: property.updated_at,
              property_id: property.id,
              icon_type: 'status_change',
            });
          }
        });
      }

      // Fetch recent deal tasks (last 14 days)
      const { data: tasksData, error: tasksError } = await (supabase as any)
        .from('deal_tasks')
        .select(`
          id,
          title,
          created_at,
          lead_id,
          deal_tracker_leads!inner(
            seller_names,
            property_addresses
          )
        `)
        .eq('seller_id', user.id)
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false })
        .limit(5);

      if (tasksError) {
        console.error('Error fetching deal tasks:', tasksError);
      } else if (tasksData) {
        (tasksData as any[]).forEach((task: any) => {
          const lead = Array.isArray(task.deal_tracker_leads) ? task.deal_tracker_leads[0] : task.deal_tracker_leads;
          allActivities.push({
            id: `task-${task.id}`,
            property_address: lead?.property_addresses || lead?.seller_names || 'Deal task',
            activity_type: 'task_created',
            description: `New task: ${task.title}`,
            timestamp: task.created_at,
            icon_type: 'task',
          });
        });
      }

      // Fetch recent deal activities (last 14 days)
      const { data: dealActivitiesData, error: dealActivitiesError } = await (supabase as any)
        .from('deal_activities')
        .select(`
          id,
          title,
          activity_type,
          created_at,
          lead_id,
          deal_tracker_leads!inner(
            seller_names,
            property_addresses
          )
        `)
        .eq('seller_id', user.id)
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false })
        .limit(5);

      if (dealActivitiesError) {
        console.error('Error fetching deal activities:', dealActivitiesError);
      } else if (dealActivitiesData) {
        (dealActivitiesData as any[]).forEach((activity: any) => {
          const lead = Array.isArray(activity.deal_tracker_leads) ? activity.deal_tracker_leads[0] : activity.deal_tracker_leads;
          const typeLabel = (activity.activity_type || '').replace(/_/g, ' ');
          allActivities.push({
            id: `deal-activity-${activity.id}`,
            property_address: lead?.property_addresses || lead?.seller_names || 'Deal activity',
            activity_type: 'activity_logged',
            description: `Logged ${typeLabel}: ${activity.title}`,
            timestamp: activity.created_at,
            icon_type: activity.activity_type || 'note',
          });
        });
      }

      // Sort all activities by timestamp (most recent first) and limit to 8
      const sortedActivities = allActivities
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 8);

      setActivities(sortedActivities);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error fetching recent activities:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentActivities();
  }, [user]);

  // Set up real-time subscriptions
  useEffect(() => {
    if (!user) return;

    let viewsChannel: ReturnType<typeof supabase.channel> | null = null;
    let propertiesChannel: ReturnType<typeof supabase.channel> | null = null;
    let tasksChannel: ReturnType<typeof supabase.channel> | null = null;
    let dealActivitiesChannel: ReturnType<typeof supabase.channel> | null = null;

    try {
      viewsChannel = supabase
        .channel('recent-activity-views')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'property_views' }, () => {
          fetchRecentActivities();
        })
        .subscribe();

      propertiesChannel = supabase
        .channel('recent-activity-properties')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'properties', filter: `seller_id=eq.${user.id}` }, () => {
          fetchRecentActivities();
        })
        .subscribe();

      tasksChannel = supabase
        .channel('recent-activity-tasks')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'deal_tasks', filter: `seller_id=eq.${user.id}` }, () => {
          fetchRecentActivities();
        })
        .subscribe();

      dealActivitiesChannel = supabase
        .channel('recent-activity-deal-activities')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'deal_activities', filter: `seller_id=eq.${user.id}` }, () => {
          fetchRecentActivities();
        })
        .subscribe();
    } catch (error) {
      console.warn('Failed to subscribe to recent activity (Safari security restriction?):', error);
    }

    return () => {
      try {
        if (viewsChannel) supabase.removeChannel(viewsChannel);
        if (propertiesChannel) supabase.removeChannel(propertiesChannel);
        if (tasksChannel) supabase.removeChannel(tasksChannel);
        if (dealActivitiesChannel) supabase.removeChannel(dealActivitiesChannel);
      } catch (error) {
        console.warn('Error cleaning up recent activity subscription:', error);
      }
    };
  }, [user]);

  return {
    activities: activities.map(activity => ({
      ...activity,
      timeAgo: formatTimeAgo(activity.timestamp),
      formattedDate: formatDate(activity.timestamp),
    })),
    isLoading,
    error,
    refetch: fetchRecentActivities,
  };
};
